<template>
  <div class="section-rediscover">
    <y-container>
      <div class="title">
        <y-text size="36px">{{ t("shell.home.rediscover.title") }}</y-text>
      </div>
      <swiper
        effect="fade"
        :navigation="{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        }"
        :modules="[Navigation, EffectFade]"
      >
        <swiper-slide v-for="(item, index) in list" :key="index">
          <div class="item item-hotelux">
            <el-image class="img" :src="item.images?.[0]" fit="cover" />
            <div class="hotel-info">
              <div class="hotel-info-content">
                <y-text class="name" size="28px" line-clamp="1">
                  {{ item.nameI18n ? getI18nText(item.nameI18n) : item.name }}
                </y-text>
                <y-text class="text" block line-clamp="5">
                  {{
                    item.descriptionI18n
                      ? getI18nText(item.descriptionI18n)
                      : item.description
                  }}
                </y-text>
              </div>
              <y-button class="book-btn" @click="handleClick(item)">
                {{ t("shell.common.book") }}
              </y-button>
            </div>
          </div>
        </swiper-slide>
      </swiper>
      <div class="arr-btns">
        <div class="btn swiper-button-prev" />
        <div class="btn swiper-button-next" />
      </div>
    </y-container>
  </div>
</template>

<script setup>
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

import { storeToRefs } from "pinia";
import { EffectFade, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { useYChannelStoreHook } from "@/distributions/consumer/y/store/channel";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { syncHotelDetailLocation } from "@/distributions/consumer/y/utils/hotelNavigation";
import { useHotels } from "@/hooks/useHotels";
import { getI18nText } from "@/plugins/i18n";
import { useUserStoreHook } from "@/store/modules/user";

const { t } = useI18n();
const router = useRouter();
const userStore = useUserStoreHook();
const store = useHotelStoreHook();
const channelStore = useYChannelStoreHook();
const { language, preferred } = storeToRefs(userStore);
const HOTEL_LIST = [
  "5b50e50312bd3f60197ea5a2",
  "640053d58a90213b2bce0432",
  "5b35e43e04f832fae1e4d9ee",
  "603fcfaa6239d56c4813a9c4"
];

const JourneyList = computed(() => {
  return channelStore.configs?.pages?.Index?.JourneyList || HOTEL_LIST;
});

const { hotels, fetchHotels } = useHotels({
  stay: store.searchModel.stay,
  filter: {
    hotels: JourneyList.value.map(item => {
      return { _id: item };
    })
  },
  preferred: preferred.value
});

const list = computed(() => {
  if (channelStore.configs?.pages?.Index?.rediscoverAsia?.hotelList) {
    return channelStore.configs.pages.Index.rediscoverAsia.hotelList;
  }
  return hotels.value;
});

const handleClick = item => {
  if (item.externalLink) {
    window.open(item.externalLink, "_blank");
    return;
  }
  store.SET_SELECTED_HOTEL(item);
  const locationQuery = syncHotelDetailLocation(store, item, language.value);
  router.push({
    path: "/hotel/detail",
    query: {
      ...locationQuery,
      checkIn: store.searchModel.stay.date.checkIn,
      checkOut: store.searchModel.stay.date.checkOut,
      numberOfAdults: store.searchModel.stay.guest.numberOfAdults,
      numberOfChildren: store.searchModel.stay.guest.numberOfChildren,
      numberOfRooms: store.searchModel.stay.guest.numberOfRooms,
      hotelId: item._id
    }
  });
};

onMounted(() => {
  fetchHotels();
});

defineOptions({
  name: "Rediscover"
});
</script>

<style lang="scss" scoped>
.section-rediscover {
  background: #faf7f3;
  .y-container {
    overflow: hidden;
    width: 1080px;
  }
  .title {
    --y-text-font-family: var(--y-title-font);
    margin-top: 100px;
    color: var(--y-text-color);
    text-align: center;
    line-height: 1;
    margin-bottom: 46px;
  }

  .swiper-slide {
    width: 100%;
    height: 400px;
    margin-bottom: 100px;
    .item {
      .img {
        width: 710px;
        height: 400px;
      }
      .hotel-info {
        width: 524px;
        height: 308px;
        background: #ffffff;
        position: absolute;
        left: 556px;
        top: 50%;
        transform: translate(0, -50%);
        padding: 36px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }

      .name {
        font-family: var(--y-title-font);
        color: #292929;
        line-height: 40px;
      }
      .text {
        font-family: var(--y-regular-font);
        font-weight: 400;
        font-size: 14px;
        color: #949494;
        line-height: 1.5;
      }
      .book-btn {
        padding: 0 40px;
        height: 40px;
        width: 124px;
        font-family: var(--y-bold-font);
        font-size: 14px;
        color: var(--y-title-text-color);
        line-height: 40px;
        text-transform: uppercase;
        --el-button-hover-bg-color: var(--y-button-border-color);
        border-color: var(--y-border-color);
        &:hover {
          --y-title-text-color: #fff;
        }
      }
    }
  }

  .arr-btns {
    --swiper-navigation-size: 16px;
    position: absolute;
    right: 0;
    bottom: 98px;
    z-index: 2;
    display: flex;
    align-items: center;
    .btn {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 1px solid #bfbfbf;
      position: static;
      + .btn {
        margin-left: 24px;
      }
      &::after {
        --swiper-navigation-size: 12px;
        color: #000000;
      }
      &.swiper-button-disabled {
        border: 1px solid rgba(191, 191, 191, 0.6);
        opacity: 1;
        &::after {
          color: #bfbfbf;
        }
      }
    }
  }
}
</style>
